import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import * as constants from "../../../constants";
import { store } from "../../../store/store";
import { refreshToken } from "../../core/refreshTockenUtil";
import { notifyUser } from "../Utils/notifyUser";

const CLIENT_TYPE_HEADER = "X-Client-Type";

type ClientSource =
  | "WEB_DESKTOP"
  | "WEB_MOBILE"
  | "ANDROID_APP"
  | "IOS_APP";

function getClientSource(): ClientSource {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return "WEB_DESKTOP";
  }

  const userAgent = navigator.userAgent || "";
  const isAndroid = /Android/i.test(userAgent);
  const isIOS = /iPhone|iPad|iPod/i.test(userAgent);
  const isWebView =
    /; wv\)|\bwv\b|WebView/i.test(userAgent) ||
    (isIOS && !/Safari/i.test(userAgent));

  if (isWebView && isAndroid) {
    return "ANDROID_APP";
  }

  if (isWebView && isIOS) {
    return "IOS_APP";
  }

  const isMobileBrowser =
    (navigator as any).userAgentData?.mobile === true ||
    /Mobi|Android|iPhone|iPad|iPod|Opera Mini|IEMobile/i.test(userAgent);

  return isMobileBrowser ? "WEB_MOBILE" : "WEB_DESKTOP";
}


export const axiosInterceptor = axios.create();

axiosInterceptor.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = store.getState().auth.jwtToken;
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    config.headers["Accept"] = "*/*"; // Always set this header
    config.headers[CLIENT_TYPE_HEADER] = getClientSource();
    return config;
  },
  (error: AxiosError) => {
    console.error("Request error:", error);
    return Promise.reject(error);
  }
);

let isServerErrorShown = false;

export const setupInterceptor = (dispatch: any, navigate: any) => {
  axiosInterceptor.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      const User = store.getState().auth.isLogin;

      if (!User) {
        window.location.reload();
      } else {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        if (
          error.response?.status === constants.STATUS_CODES.UNAUTHORIZED &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true;
          const homeAccount = store.getState().auth.homeAccountId;

          try {
            const response = await refreshToken(homeAccount, dispatch, navigate);
            const newToken = response?.data?.data?.accessToken;
            axiosInterceptor.defaults.headers["Authorization"] = `Bearer ${newToken}`;
            return axiosInterceptor(originalRequest);
          } catch (refreshError) {
            console.error("Error refreshing token", refreshError);
            return Promise.reject(refreshError);
          }
        } 
        if (
          error.response?.status &&
          error.response.status > 500 &&
          !isServerErrorShown
        ) {
          isServerErrorShown = true; 
          notifyUser(
            "We're having trouble connecting to the server. Please try again later.",
            "error",
            ""
          ); 
          setTimeout(() => {
            isServerErrorShown = false;
          }, 5000);
        }
      }

      return Promise.reject(error);
    }
  );
};
